import { useEffect, useState } from 'react'
import { useAppSelector } from '../hooks/redux'
import {
  cancelPortalBooking,
  deletePortalBooking,
  fetchPortalBookings,
  type PortalBooking,
  updatePortalBookingRequests,
} from '../services/guestPortalSupabase'

type GuestBookingRecord = PortalBooking

const formatCurrency = (amount: number) => `R${amount.toLocaleString()}`
const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A')
const normalizeStatus = (value: unknown, fallback = 'pending') => String(value || fallback).toLowerCase()

const loadGuestBookingsFromStorage = (email?: string) => {
  try {
    const raw = localStorage.getItem('guestBookings')
    const parsed = raw ? JSON.parse(raw) : []
    const all: GuestBookingRecord[] = Array.isArray(parsed) ? parsed : []
    return email ? all.filter((booking) => booking.guestEmail === email) : all
  } catch {
    return []
  }
}

const sortBookingsByDateDesc = (items: GuestBookingRecord[]) => {
  return items
    .slice()
    .sort((left: GuestBookingRecord, right: GuestBookingRecord) => new Date(right.bookedDate || 0).getTime() - new Date(left.bookedDate || 0).getTime())
}

const statusBadge = (status: string) => {
  const s = status.toLowerCase()
  if (s === 'confirmed') return 'bg-emerald-100 text-emerald-800'
  if (s === 'cancelled') return 'bg-red-100 text-red-800'
  return 'bg-amber-100 text-amber-800'
}

const paymentBadge = (status: string) => {
  const s = status.toLowerCase()
  if (s === 'paid') return 'bg-sky-100 text-sky-800'
  if (s === 'cancelled') return 'bg-red-100 text-red-800'
  return 'bg-amber-100 text-amber-800'
}

export default function GuestBookingsPage() {
  const { user } = useAppSelector((state) => state.auth)
  const [bookings, setBookings] = useState<GuestBookingRecord[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editRequests, setEditRequests] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const loadBookings = async () => {
    if (!user?.email) {
      setBookings([])
      return
    }

    setLoading('load')
    try {
      const mapped = await fetchPortalBookings()

      setBookings(sortBookingsByDateDesc(mapped))
      localStorage.setItem('guestBookings', JSON.stringify(mapped))
    } catch (error) {
      console.error('Failed to load guest bookings from Supabase:', error)
      setBookings(sortBookingsByDateDesc(loadGuestBookingsFromStorage(user.email)))
      showMessage('error', 'Showing locally saved bookings because live booking history could not be loaded.')
    } finally {
      setLoading(null)
    }
  }

  useEffect(() => { void loadBookings() }, [user?.email])

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 4000)
  }

  // ── Edit special requests ──────────────────────────────────────────────
  const startEdit = (booking: GuestBookingRecord) => {
    setEditingId(booking.id)
    setEditRequests(booking.specialRequests || '')
  }

  const saveEdit = async (booking: GuestBookingRecord) => {
    setLoading('edit-' + booking.id)

    if (!booking.confirmationNumber) {
      setLoading(null)
      showMessage('error', 'This booking reference is missing.')
      return
    }

    try {
      await updatePortalBookingRequests(booking.confirmationNumber, editRequests)
    } catch (error) {
      setLoading(null)
      showMessage('error', error instanceof Error ? error.message : 'Failed to update special requests.')
      return
    }

    await loadBookings()
    setEditingId(null)
    setLoading(null)
    showMessage('success', 'Special requests updated.')
  }

  // ── Cancel booking ─────────────────────────────────────────────────────
  const cancelBooking = async (booking: GuestBookingRecord) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return
    setLoading('cancel-' + booking.id)

    if (!booking.confirmationNumber) {
      setLoading(null)
      showMessage('error', 'This booking reference is missing.')
      return
    }

    try {
      await cancelPortalBooking(booking.confirmationNumber)
    } catch (error) {
      setLoading(null)
      showMessage('error', error instanceof Error ? error.message : 'Failed to cancel booking.')
      return
    }

    await loadBookings()
    setLoading(null)
    showMessage('success', 'Booking cancelled.')
  }

  // ── Delete booking ─────────────────────────────────────────────────────
  const deleteBooking = async (booking: GuestBookingRecord) => {
    setLoading('delete-' + booking.id)

    if (!booking.confirmationNumber) {
      setLoading(null)
      showMessage('error', 'This booking reference is missing.')
      return
    }

    try {
      await deletePortalBooking(booking.confirmationNumber)
    } catch (error) {
      setLoading(null)
      showMessage('error', error instanceof Error ? error.message : 'Failed to delete booking.')
      return
    }

    await loadBookings()
    setConfirmDeleteId(null)
    setLoading(null)
    showMessage('success', 'Booking removed from your history.')
  }

  const isCancelled = (b: GuestBookingRecord) =>
    normalizeStatus(b.bookingStatus || b.status, '') === 'cancelled'

  return (
    <div className="space-y-8">
      {/* Header */}
      <section className="rounded-3xl border border-sky-100 bg-white/95 p-8 shadow-lg backdrop-blur">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">My Bookings</p>
            <h2 className="text-3xl font-bold text-slate-900">Manage Reservations</h2>
            <p className="mt-2 text-slate-600">Edit, cancel, or remove your booking records below.</p>
          </div>
          <div className="rounded-2xl bg-sky-50 px-5 py-4 border border-sky-100 text-center">
            <div className="text-sm text-slate-500">Total Bookings</div>
            <div className="mt-1 text-3xl font-bold text-slate-900">{bookings.length}</div>
          </div>
        </div>
      </section>

      {/* Feedback message */}
      {message && (
        <div className={`rounded-2xl px-6 py-4 font-medium text-sm ${message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Booking list */}
      <section className="rounded-3xl border border-slate-200 bg-white/95 p-8 shadow-lg backdrop-blur">
        {loading === 'load' ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center text-slate-500">
            Loading your bookings...
          </div>
        ) : bookings.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
            <div className="mb-3 text-5xl">🧳</div>
            <h4 className="text-xl font-semibold text-slate-900">No bookings yet</h4>
            <p className="mt-2 text-slate-500">Your reservations will appear here after completing a booking.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {bookings.map((booking) => {
              const bookingSt = normalizeStatus(booking.bookingStatus || booking.status)
              const paymentSt = normalizeStatus(booking.paymentStatus)
              const cancelled = isCancelled(booking)

              return (
                <article
                  key={booking.id}
                  className={`rounded-2xl border p-6 transition-colors ${cancelled ? 'border-red-200 bg-red-50/30 opacity-75' : 'border-slate-200 hover:border-sky-200 hover:bg-sky-50/30'}`}
                >
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    {/* Left: Info */}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="text-xl font-bold text-slate-900">{booking.roomName || 'Marina Glen Stay'}</h4>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusBadge(bookingSt)}`}>{bookingSt}</span>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${paymentBadge(paymentSt)}`}>Payment {paymentSt}</span>
                      </div>
                      <p className="text-sm text-slate-500 mb-4">Ref: {booking.confirmationNumber || '—'}</p>

                      <div className="grid gap-2 sm:grid-cols-2 text-sm text-slate-600">
                        <div><strong>Check-in:</strong> {formatDate(booking.checkIn)}</div>
                        <div><strong>Check-out:</strong> {formatDate(booking.checkOut)}</div>
                        <div><strong>Guests:</strong> {booking.guests || 1}</div>
                        <div><strong>Payment:</strong> {(booking.method || 'pending').toUpperCase()}</div>
                      </div>

                      {/* Special requests display / edit */}
                      {editingId === booking.id ? (
                        <div className="mt-4">
                          <label className="block text-sm font-semibold text-slate-700 mb-1">Special Requests</label>
                          <textarea
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                            rows={3}
                            value={editRequests}
                            onChange={(e) => setEditRequests(e.target.value)}
                            placeholder="Dietary requirements, room preferences, arrival time…"
                          />
                          <div className="mt-3 flex gap-3">
                            <button
                              onClick={() => saveEdit(booking)}
                              disabled={loading === 'edit-' + booking.id}
                              className="rounded-xl bg-sky-600 px-5 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                            >
                              {loading === 'edit-' + booking.id ? 'Saving…' : 'Save Changes'}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="rounded-xl border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : booking.specialRequests ? (
                        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
                          <strong>Special Requests:</strong> {booking.specialRequests}
                        </div>
                      ) : null}
                    </div>

                    {/* Right: Amount + actions */}
                    <div className="flex flex-col items-end gap-4 min-w-[160px]">
                      <div className="rounded-2xl bg-slate-900 px-5 py-4 text-white text-center w-full">
                        <div className="text-xs uppercase tracking-[0.24em] text-slate-300">Total</div>
                        <div className="mt-1 text-2xl font-bold">{formatCurrency(Number(booking.amount || 0))}</div>
                      </div>

                      {!cancelled && (
                        <div className="flex flex-col gap-2 w-full">
                          {editingId !== booking.id && (
                            <button
                              onClick={() => startEdit(booking)}
                              className="w-full rounded-xl border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100 transition-colors"
                            >
                              ✏️ Edit Requests
                            </button>
                          )}
                          <button
                            onClick={() => cancelBooking(booking)}
                            disabled={loading === 'cancel-' + booking.id}
                            className="w-full rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-60"
                          >
                            {loading === 'cancel-' + booking.id ? 'Cancelling…' : '🚫 Cancel Booking'}
                          </button>
                        </div>
                      )}

                      {/* Delete confirmation */}
                      {confirmDeleteId === booking.id ? (
                        <div className="w-full rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-center">
                          <p className="text-xs font-semibold text-red-800 mb-2">Remove from history?</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => deleteBooking(booking)}
                              disabled={loading === 'delete-' + booking.id}
                              className="flex-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-60"
                            >
                              {loading === 'delete-' + booking.id ? '…' : 'Yes, Delete'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="flex-1 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                            >
                              Keep
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(booking.id)}
                          className="w-full rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-500 hover:bg-red-50 hover:border-red-300 transition-colors"
                        >
                          🗑️ Delete Record
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
